import { FC, useMemo } from 'react';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import type { ChannelMarginRow } from '../margin.types';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartTitle, DrillToolbar, TablePanel } from '../Forecasting.styles';
import { FORECASTING_TABLE_CSS } from '../forecastingTableStyles';
import { DrillHeader } from './DrillHeader';

function currencyCell(value: number) {
  return <span className="forecasting-currency-cell">{formatCurrency(value)}</span>;
}

interface ChannelMarginsTableProps {
  data: ChannelMarginRow[];
  levelTitle: string;
  canDrillUp: boolean;
  canDrillDown: boolean;
  onDrillUp: () => void;
  onDrillDown: () => void;
  selectedKey: string | null;
  onRowSelect: (row: ChannelMarginRow) => void;
}

export const ChannelMarginsTable: FC<ChannelMarginsTableProps> = ({
  data,
  levelTitle,
  canDrillUp,
  canDrillDown,
  onDrillUp,
  onDrillDown,
  selectedKey,
  onRowSelect
}) => {
  const columns: ColumnsType<ChannelMarginRow> = useMemo(
    () => [
      {
        title: (
          <DrillHeader
            title={levelTitle}
            canUp={canDrillUp}
            canDown={canDrillDown}
            onDrillUp={onDrillUp}
            onDrillDown={onDrillDown}
          />
        ),
        dataIndex: 'label',
        key: 'label',
        ellipsis: true,
        width: 120
      },
      {
        title: 'Booked',
        dataIndex: 'booked',
        key: 'booked',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'Pipe',
        dataIndex: 'pipe',
        key: 'pipe',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'B + P',
        dataIndex: 'bookedPlusPipe',
        key: 'bookedPlusPipe',
        align: 'right',
        width: 100,
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.bookedPlusPipe - b.bookedPlusPipe,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'Pre-Pipe',
        dataIndex: 'prePipe',
        key: 'prePipe',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'Hard Costs',
        dataIndex: 'hardCosts',
        key: 'hardCosts',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'Rebate',
        dataIndex: 'rebate',
        key: 'rebate',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'GP',
        dataIndex: 'gp',
        key: 'gp',
        align: 'right',
        width: 100,
        render: (v: number) => currencyCell(v)
      },
      {
        title: 'Margin',
        dataIndex: 'margin',
        key: 'margin',
        align: 'right',
        width: 80,
        render: (v: number) => formatPercent(v, 0)
      }
    ],
    [levelTitle, canDrillUp, canDrillDown, onDrillUp, onDrillDown]
  );

  const totals = data.reduce(
    (acc, row) => ({
      booked: acc.booked + row.booked,
      pipe: acc.pipe + row.pipe,
      bookedPlusPipe: acc.bookedPlusPipe + row.bookedPlusPipe,
      prePipe: acc.prePipe + row.prePipe,
      hardCosts: acc.hardCosts + row.hardCosts,
      rebate: acc.rebate + row.rebate,
      gp: acc.gp + row.gp
    }),
    { booked: 0, pipe: 0, bookedPlusPipe: 0, prePipe: 0, hardCosts: 0, rebate: 0, gp: 0 }
  );
  const totalRev = totals.bookedPlusPipe + totals.prePipe;

  return (
    <TablePanel>
      <DrillToolbar>
        <ChartTitle style={{ margin: 0 }}>{levelTitle} margins</ChartTitle>
        <div className="drill-actions">
          <span className="drill-hint">Drill level</span>
          <Tooltip title="Drill up">
            <button
              type="button"
              className="drill-btn"
              disabled={!canDrillUp}
              onClick={onDrillUp}
              aria-label="Drill up"
            >
              <CaretUpOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Drill down">
            <button
              type="button"
              className="drill-btn"
              disabled={!canDrillDown}
              onClick={onDrillDown}
              aria-label="Drill down"
            >
              <CaretDownOutlined />
            </button>
          </Tooltip>
        </div>
      </DrillToolbar>
      <Table<ChannelMarginRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={columns}
        dataSource={data}
        scroll={{ x: 980, y: 320 }}
        className="forecasting-bp-table"
        onRow={(record) => ({
          onClick: () => onRowSelect(record),
          style: { cursor: 'pointer' }
        })}
        rowClassName={(record) => (record.key === selectedKey ? 'forecasting-row-selected' : '')}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>Grand total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                {currencyCell(totals.booked)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                {currencyCell(totals.pipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                {currencyCell(totals.bookedPlusPipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                {currencyCell(totals.prePipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                {currencyCell(totals.hardCosts)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                {currencyCell(totals.rebate)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                {currencyCell(totals.gp)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                {formatPercent(totalRev > 0 ? (totals.gp / totalRev) * 100 : 0, 0)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <style>{FORECASTING_TABLE_CSS}</style>
    </TablePanel>
  );
};
